import { useState } from 'react';
import { useForm } from '@inertiajs/react';
import { PublicLayout } from '@/components/PublicLayout';

interface SubscribeProps {
  success?: boolean;
  message?: string;
  error?: string;
  auth?: {
    user?: {
      id: number;
      email: string;
      name?: string;
      username?: string;
      role?: 'user' | 'admin' | 'manager';
    };
  };
}

export function Subscribe({ success = false, message = '', error = '', auth }: SubscribeProps) {
  const { data, setData, post, processing, errors } = useForm({
    email: '',
    name: ''
  });

  const [submitted, setSubmitted] = useState(success);
  const [submitMessage, setSubmitMessage] = useState(message);
  const [submitError, setSubmitError] = useState(error);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    post('/blog/subscribe', {
      onSuccess: (page) => {
        const props = page.props as any;
        setSubmitted(true);
        if (props.success) {
          setSubmitMessage(props.message || 'Thanks for subscribing!');
          setSubmitError('');
        } else {
          setSubmitError(props.error || 'Something went wrong');
          setSubmitMessage('');
        }
      },
      onError: (err) => {
        setSubmitError(err.email || 'Something went wrong');
        setSubmitMessage('');
      }
    });
  };

  if (submitted && submitMessage && !submitError) {
    return (
      <PublicLayout user={auth?.user}>
        <div className="bg-white rounded-lg shadow-sm border p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">
              {submitMessage}
            </h3>
            <p className="text-sm text-muted-foreground">
              Check your inbox for a confirmation email.
            </p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout user={auth?.user}>
      <div className="bg-white rounded-lg shadow-sm border p-8">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold">
          Subscribe to our newsletter
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Get the latest blog posts delivered directly to your inbox.
        </p>
      </div>

      {submitError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium">
            Name (optional)
          </label>
          <input
            type="text"
            id="name"
            value={data.name}
            onChange={(e) => setData('name', e.target.value)}
            className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            placeholder="Your name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium">
            Email address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            required
            value={data.email}
            onChange={(e) => setData('email', e.target.value)}
            className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={processing}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>

      <p className="mt-4 text-xs text-muted-foreground text-center">
        We respect your privacy. Unsubscribe at any time.
      </p>
      </div>
    </PublicLayout>
  );
}

export default Subscribe;
